import {
  Box,
  ColumnLayout,
  FormField,
  Input,
  RadioGroup,
  Select,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import {
  addBlankDescription,
  CODE_MASTER,
  COMMON_LABELS,
  DivisionDictDescription,
} from "../default-constants";
import { CONSTRAINT_TEXT, IErrMsg, LABELS } from "./constants";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  useGetFinancialInstitutionAllQuery,
  useGetFinancialInstitutionBranchQuery,
} from "../../features/api";
import {
  getFinancialInstitutionBranchItems,
  getFinancialInstitutionItems,
} from "../../utils/common";
import { BankAccountsUpdateRequest } from "../../features/api-interfaces";
import { isEmpty } from "lodash";
import { OptionDefinition } from "@cloudscape-design/components-themed/internal/components/option/interfaces";
import {
  validRequired,
  validBankAccountHolderKana,
  validBankAccountNumber,
} from "./validation";

interface IBankAccountUpdate {
  request: BankAccountsUpdateRequest;
  setRequest: Dispatch<SetStateAction<BankAccountsUpdateRequest>>;
  errMsg: IErrMsg;
  setErrMsg: Dispatch<SetStateAction<IErrMsg>>;
}

export const BankAccountUpdate = (props: IBankAccountUpdate) => {
  const { request, setRequest, errMsg, setErrMsg } = props;
  const [banks, setBanks] = useState([] as DivisionDictDescription[]);
  const [branches, setBranches] = useState([] as DivisionDictDescription[]);
  const { data: financialInstitution } = useGetFinancialInstitutionAllQuery();
  const { data: financialInstitutionBranch } =
    useGetFinancialInstitutionBranchQuery(request.fnnInsMthStrCd, {
      skip: isEmpty(request.fnnInsMthStrCd),
    });

  useEffect(() => {
    setBanks(
      addBlankDescription(getFinancialInstitutionItems(financialInstitution))
    );
  }, [financialInstitution]);

  useEffect(() => {
    setBranches(
      addBlankDescription(
        getFinancialInstitutionBranchItems(financialInstitutionBranch)
      )
    );
  }, [financialInstitutionBranch]);

  /**
   * 金融機関の入力ハンドラ.
   * @param {OptionDefinition} selectedOption 入力値
   */
  const bankOnChange = (selectedOption: OptionDefinition) => {
    const inputValue = selectedOption.value ?? "";
    setRequest({
      ...request,
      fnnInsMthStrCd: inputValue,
      fnnInsMthStrNm: selectedOption.label ?? "",
      fnnInsBrnCd: "",
      fnnInsBrnNm: "",
    });
    setErrMsg({ ...errMsg, fnnInsMthStrCd: validRequired(inputValue) });
  };

  /**
   * 支店の入力ハンドラ.
   * @param {OptionDefinition} selectedOption 入力値
   */
  const branchOnChange = (selectedOption: OptionDefinition) => {
    const inputValue = selectedOption.value ?? "";
    setRequest({
      ...request,
      fnnInsBrnCd: inputValue,
      fnnInsBrnNm: selectedOption.label ?? "",
    });
    setErrMsg({ ...errMsg, fnnInsBrnCd: validRequired(inputValue) });
  };

  /**
   * 口座番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const bankAccountNumberOnChange = (inputValue: string) => {
    setRequest({
      ...request,
      bankAccountNumber: inputValue,
    });
    setErrMsg({
      ...errMsg,
      bankAccountNumber: validBankAccountNumber(inputValue),
    });
  };

  /**
   * 口座名義カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const bankAccountHolderKanaOnClick = (inputValue: string) => {
    setRequest({
      ...request,
      bankAccountHolderKana: inputValue,
    });
    setErrMsg({
      ...errMsg,
      bankAccountHolderKana: validBankAccountHolderKana(inputValue),
    });
  };

  return (
    <SpaceBetween direction={"vertical"} size={"l"}>
      <ColumnLayout columns={3}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.BANK}</Box>
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg.fnnInsMthStrCd}
        >
          <Select
            selectedOption={{
              value: request.fnnInsMthStrCd,
              label: request.fnnInsMthStrNm,
            }}
            options={banks}
            onChange={({ detail: { selectedOption } }) =>
              bankOnChange(selectedOption)
            }
            filteringType={"auto"}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={3}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.BRANCH}</Box>
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg.fnnInsBrnCd}
        >
          <Select
            selectedOption={{
              value: request.fnnInsBrnCd,
              label: request.fnnInsBrnNm,
            }}
            options={branches}
            filteringType={"auto"}
            onChange={({ detail: { selectedOption } }) =>
              branchOnChange(selectedOption)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={3}>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_TYPE}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
          >
            <RadioGroup
              items={CODE_MASTER["BANK_ACCOUNT_TYPE"]}
              onChange={({ detail: { value } }) =>
                setRequest({
                  ...request,
                  bankAccountType: Number(value),
                })
              }
              value={String(request.bankAccountType)}
            />
          </FormField>
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_NUMBER}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
            constraintText={CONSTRAINT_TEXT.BANK_ACCOUNT_NUMBER}
            errorText={errMsg.bankAccountNumber}
          >
            <Input
              value={request.bankAccountNumber}
              onChange={({ detail: { value } }) =>
                bankAccountNumberOnChange(value)
              }
            />
          </FormField>
          <FormField
            label={
              <SpaceBetween direction={"horizontal"} size={"xs"}>
                <Box variant={"strong"}>{LABELS.BANK_ACCOUNT_HOLDER_KANA}</Box>
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              </SpaceBetween>
            }
            constraintText={CONSTRAINT_TEXT.BANK_ACCOUNT_HOLDER_KANA}
            errorText={errMsg.bankAccountHolderKana}
          >
            <Input
              value={request.bankAccountHolderKana}
              onChange={({ detail: { value } }) =>
                bankAccountHolderKanaOnClick(value)
              }
            />
          </FormField>
        </SpaceBetween>
      </ColumnLayout>
    </SpaceBetween>
  );
};
